import { useCallback } from 'react';

import { LinkVariant, ModalConfirmButton, ModalFooterPosition, ModalLink, RemoveIcon } from '@dreryk/react-components';

import { prescriptionTranslationKeys } from '../../../../../translations';
import { DraftPrescriptionsFormController } from '../../draftPrescriptions/DraftPrescriptionsForm.types';
import { DraftStandardPrescriptionFieldName } from '../../draftPrescriptions/standardPrescription/DraftStandardPrescriptionField.types';

import { ExtendedDosageFormBean, ExtendedDosageFormController } from './form/ExtendedDosageForm.types';
import { mapFormBeanToExtendedDosage } from './extendedDosage.utils';

type ExtendedDosageModalConfirmButtonProps = Pick<ExtendedDosageFormController, 'handleSubmit'> &
   Pick<DraftPrescriptionsFormController, 'setValue'> & {
      readonly name: DraftStandardPrescriptionFieldName;
      readonly dosageFieldName: DraftStandardPrescriptionFieldName;
      readonly saveBlocked: boolean;
      readonly onClose: VoidFunction;
   };

export const ExtendedDosageModalConfirmButton = (props: ExtendedDosageModalConfirmButtonProps): JSX.Element => {
   const save = useCallback(
      (bean: ExtendedDosageFormBean) => {
         props.onClose();
         props.setValue(props.name, mapFormBeanToExtendedDosage(bean), { shouldValidate: true, shouldDirty: true });
         props.setValue(props.dosageFieldName, bean.patientInstruction, {
            shouldValidate: true,
            shouldDirty: true,
         });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.setValue, props.name, props.dosageFieldName, props.onClose]
   );

   return (
      <ModalConfirmButton
         label={prescriptionTranslationKeys.extendedDosage.save}
         onClick={props.handleSubmit(save)}
         disabled={props.saveBlocked}
      />
   );
};

type ExtendedDosageRemoveButtonProps = Pick<DraftPrescriptionsFormController, 'setValue'> & {
   readonly name: DraftStandardPrescriptionFieldName;
   readonly onClose: VoidFunction;
};

export const ExtendedDosageRemoveButton = (props: ExtendedDosageRemoveButtonProps): JSX.Element => {
   const handleRemove = useCallback(() => {
      props.onClose();
      props.setValue(props.name, null, { shouldValidate: true, shouldDirty: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.setValue, props.name, props.onClose]);

   return (
      <ModalLink
         testId={'removeExtendedDosage'}
         icon={<RemoveIcon />}
         variant={LinkVariant.ERROR}
         label={prescriptionTranslationKeys.extendedDosage.removeExtendedDosage}
         onClick={handleRemove}
         position={ModalFooterPosition.SUPPLEMENTARY}
      />
   );
};
